import React, { useState } from 'react'
import {
  DataGrid,
  useGridApiRef
} from '@mui/x-data-grid'
import notFound from "../../containers/Contacts/Table/NotFoundData"
import DataGridSkeletonOverlay from "../../components/Skeleton/DataGridSkeletonOverlay"
import { handleTableId } from "../../helpers/helper"
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal"
import { showSnackBar } from "../../components/Snackbar/Snackbar"
import { useMyContextLoading } from "../../components/LoadingContextProvider/LoadingContextProvider"
import { useDispatch } from "react-redux"
import { removeAffiliateGroupsAdmin, resendAffiliateGroupsAdminInvite } from "../../features/affiliateGroupsAdmins/affiliateGroupsAdmins"
import { useNavigate } from "react-router-dom"
import UserStatusCell from "../Contacts/Table/Contact/StatusCell"
import { LuPencil, LuRotateCw, LuTrash2 } from "react-icons/lu"
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog"
import { useUiKit } from "../../components/UiKit/UiKitContext"
import { failureOf } from "../../utils/errorHelpers"

const AffiliateGroupsAdminsTable = ({ data, setIsUpdated, isUpdated }) => {
  const apiRef = useGridApiRef()
  const [openDeleteTaskModal, setOpenDeleteTaskModal] = useState("")
  const { triggerLoading, isBarLoading } = useMyContextLoading()
  const { confirm: kitConfirm } = useUiKit()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const deleteAffiliateGroupsAdminOrThrow = async (userId) => {
    const response = await dispatch(removeAffiliateGroupsAdmin(userId))
    if (!response?.payload?.data?._id) {
      throw failureOf(response, 'Something went wrong, please try again')
    }
    setIsUpdated(!isUpdated)
    showSnackBar("success", "Affiliate Groups Admin successfully deleted")
  }

  const deleteAffiliateGroupsAdmin = async (userId) => {
    triggerLoading(true)
    try {
      const response = await dispatch(removeAffiliateGroupsAdmin(userId))
      if (response?.payload?.data?._id) {
        setIsUpdated(!isUpdated)
        showSnackBar("success", "Affiliate Groups Admin successfully deleted")
      } else {
        showSnackBar("error", "Something went wrong, please try again")
      }
    } catch (error) {
      console.error(error)
    }
    triggerLoading(false)
  }

  const handleResendInvite = async (userId) => {
    triggerLoading(true)
    try {
      const response = await dispatch(resendAffiliateGroupsAdminInvite(userId))
      if (response?.payload?.data?.success) {
        showSnackBar("success", "Invitation email has been resent")
      } else {
        showSnackBar("error", response?.payload || "Failed to resend invitation")
      }
    } catch (error) {
      console.error(error)
    }
    triggerLoading(false)
  }

  const columns = [
    {
      "field": "_id",
      "headerName": "Admin Id",
      renderCell: (params) => {
        return (
          <div className="text-primary-main font-semibold">
            {handleTableId(params.row?._id)}
          </div>
        )
      },
      sortable: false,
      // minWidth guard: flex has no floor, so a crowded grid clamps this to MUI's 50px default and clips the id + "ADMIN ID" header
      flex: 1,
      minWidth: 130,
    },
    {
      "field": "name",
      "headerName": "Name",
      renderCell: (params) => {
        return (
          <div key={params?.row?.id} className="flex w-full">
            {params?.row?.firstName} {params?.row?.lastName}
          </div>
        )
      },
      sortable: false,
      // minWidth guard so the "NAME" header + full name never clamp to the 50px flex floor
      flex: 1,
      minWidth: 160,
    },
    {
      "field": "email",
      "headerName": "Email",
      sortable: false,
      // minWidth guard: email is a plain flex string with no truncate; keep it from clamping below a readable width
      flex: 1,
      minWidth: 200,
    },
    {
      field: "visibleGroups",
      headerName: "Visible Groups",
      renderCell: (params) => {
        const groups = params?.row?.visibleGroups || []
        return (
          <div className="flex flex-wrap gap-1">
            {groups.length > 0 ? (
              groups.map((group) => (
                <span key={group._id} className="px-2 py-0.5 bg-primary-main/10 text-primary-main rounded text-xs">
                  {group.name}
                </span>
              ))
            ) : (
              <span className="text-text-secondary">No groups assigned</span>
            )}
          </div>
        )
      },
      sortable: false,
      // widest column keeps flex:1.5 to absorb slack; minWidth guards the wrapping chips + "VISIBLE GROUPS" header
      flex: 1.5,
      minWidth: 220,
    },
    {
      "field": "status",
      "headerName": "Status",
      renderCell: (params) => {
        return (
          <div>
            {
              params?.row?.isPassword ?
                <UserStatusCell statusObj={{ bgColor: 'var(--color-success-background)', textColor: 'var(--color-success-text)', label: 'Active' }} />
                :
                <UserStatusCell statusObj={{ bgColor: 'var(--color-warning-background)', textColor: 'var(--color-warning-text)', label: 'Pending' }} />
            }
          </div>
        )
      },
      sortable: false,
      // minWidth guard so the "STATUS" header + status badge don't clamp to 50px ("STA...") when crowded
      flex: 1,
      minWidth: 120,
    },
    {
      "field": "action",
      "headerName": "Action",
      // icon-only column: fixed width (no flex) sized to clear the uppercase "ACTION" header + up to 3 action icons
      width: 150,
      headerAlign: 'right',
      align: 'right',
      renderCell: (params) => {
        const removeFunc = () => deleteAffiliateGroupsAdmin(params?.row?._id)
        return (
          <div key={params?.row?.id} className="flex items-center justify-end gap-3 w-full pr-1">
            {!params?.row?.isPassword && (
              <div className="cursor-pointer" title="Resend Invite" onClick={() => handleResendInvite(params?.row?._id)}>
                <LuRotateCw size={18} className="text-text-secondary" />
              </div>
            )}
            <div className="cursor-pointer" onClick={() => {
              navigate(`/affiliate-groups-admins/edit/${params?.row?._id}`)
            }}>
              <LuPencil size={18} className="text-text-secondary" />
            </div>

            <div className="cursor-pointer" onClick={() => setOpenDeleteTaskModal(params?.row?._id)}>
              <LuTrash2 size={18} className="text-text-secondary hover:text-error-main" />
            </div>
            {openDeleteTaskModal === params?.row?._id && (kitConfirm ?
              <ConfirmDialog
                open={Boolean(openDeleteTaskModal)}
                onClose={() => setOpenDeleteTaskModal("")}
                title={`Delete ${[params?.row?.firstName, params?.row?.lastName].filter(Boolean).join(' ') || 'this affiliate groups admin'}?`}
                impact={["The affiliate groups admin account is removed from the list.", "This cannot be undone."]}
                confirmLabel="Delete admin"
                tone="danger"
                onConfirm={() => deleteAffiliateGroupsAdminOrThrow(params?.row?._id)}
              /> :
              <ConfirmModal leftTopIcon={<LuTrash2 size={18} className="text-text-secondary" />}
                title="Are you sure to delete the Affiliate Groups Admin?"
                modalButtonTitle={"Delete"} classes=""
                open={openDeleteTaskModal}
                setOpen={setOpenDeleteTaskModal}
                functionButton={removeFunc}
              >
                You will not be able to restore it later
              </ConfirmModal>)}
          </div>
        )
      },
      sortable: false,
    }
  ]

  return (
    <div>
      <DataGrid
        sx={{
          border: 'none',
          '& .MuiDataGrid-columnHeaders': {
            '.MuiButtonBase-root[aria-haspopup="menu"]': {
              display: 'none'
            }
          },
          '.MuiDataGrid-virtualScroller': {
            minHeight: data?.length === 0 ? 300 : ''
          }
        }}
        rowHeight={75}
        columns={columns}
        slots={{
          noRowsOverlay: notFound,
          loadingOverlay: DataGridSkeletonOverlay,
        }}
        loading={isBarLoading && !(data?.length > 0)}
        columnVisibilityModel={{
          id: false
        }}
        rows={data}
        apiRef={apiRef}
        getRowId={(row) => row._id}
        hideFooterPagination={true}
        checkboxSelection={false}
        disableRowSelectionOnClick
        disableColumnSelector
        getRowClassName={(params) => params?.row?.status === 'Failed' ? 'bg-error-light' : ''}
      />
    </div>
  )
}

export default AffiliateGroupsAdminsTable
